import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { authAxios } from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function AuditLog() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actorFilter, setActorFilter] = useState('all');
  const [targetFilter, setTargetFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [user, actorFilter, targetFilter, actionFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load users for filters
      const usersRes = await authAxios.get(`${API}/users`);
      setUsers(usersRes.data);

      // Load audit events with filters
      const params = new URLSearchParams();
      if (actorFilter !== 'all') params.append('actor_user_id', actorFilter);
      if (targetFilter !== 'all') params.append('target_user_id', targetFilter);
      if (actionFilter !== 'all') params.append('action_type', actionFilter);
      
      const eventsRes = await authAxios.get(`${API}/audit-events?${params}`);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Failed to load audit log:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getActionDescription = (event) => {
    switch (event.action_type) {
      case 'invite_sent':
        return `Invited ${event.after_json?.email}`;
      case 'invite_accepted':
        return `Accepted invitation`;
      case 'user_updated':
        return 'Updated profile';
      case 'seasons_added':
        return `Added ${event.after_json?.season_ids?.length || 0} season(s)`;
      case 'seasons_removed':
        return `Removed ${event.after_json?.season_ids?.length || 0} season(s)`;
      default:
        return event.action_type;
    }
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'Actor', 'Target', 'Action', 'Details'];
    const rows = events.map(event => [
      formatDate(event.created_at),
      event.actor_name || 'System',
      event.target_name || '-',
      event.action_type,
      getActionDescription(event)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const actionTypes = [
    'invite_sent',
    'invite_accepted',
    'user_updated',
    'seasons_added',
    'seasons_removed'
  ];

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You need admin privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <Button variant="outline" onClick={() => navigate('/admin/players')} className="mb-4">
              ← Back to Players
            </Button>
            <h1 className="text-5xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Audit Log
            </h1>
            <p className="text-lg text-slate-600">Track all administrative actions and changes</p>
          </div>

          <Button onClick={exportToCSV} variant="outline">
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Acting Admin</label>
                <Select value={actorFilter} onValueChange={setActorFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Admins" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Admins</SelectItem>
                    {users.filter(u => u.role === 'admin').map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Affected User</label>
                <Select value={targetFilter} onValueChange={setTargetFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Action Type</label>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {actionTypes.map(type => (
                      <SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Acting Admin</th>
                      <th>Affected User</th>
                      <th>Action</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-12 text-slate-600">
                          No audit events found
                        </td>
                      </tr>
                    ) : (
                      events.map(event => (
                        <tr key={event.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-4 text-sm">{formatDate(event.created_at)}</td>
                          <td className="py-4 font-medium">{event.actor_name || 'System'}</td>
                          <td className="py-4">{event.target_name || '-'}</td>
                          <td className="py-4">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              {event.action_type.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="py-4 text-sm text-slate-600">
                            {getActionDescription(event)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
